import React, { useState, useContext, useEffect } from "react";
import { UserContext } from "../../utils/userContext";
import axios from "../../utils/axiosInstance";
import { toCloudinaryDeliveryUrl } from "../../utils/cloudinaryMedia";
import { useT } from "../../contexts/TranslationContext";
import Button from "../ui/Button";
import HoursProgressBar from "../ui/HoursProgressBar";
import SubTaskListEditor from "../TaskManagement/editors/SubTaskListEditor";
import SubTaskPickerModal from "../TaskManagement/pickers/SubTaskPickerModal";

// Helper functions to convert between project format and editor format
const projectSubtaskToEditor = (subtask) => {
  return {
    id: subtask._id || subtask.id,
    _id: subtask._id,
    name: subtask.title || subtask.name || "",
    description: subtask.description || "",
    weight: subtask.weight || 1,
    estimatedHours: subtask.estimatedHours,
    registryId: subtask.registryRef?.id,
    showDescriptionInTimeReport: subtask.showDescriptionInTimeReport || false,
    requireDescriptionInTimeReport: subtask.requireDescriptionInTimeReport || false,
    sortOrder: subtask.sortOrder,
    billingModel: subtask.billingModel || "",
    hourlyRate: subtask.hourlyRate ?? "",
    fixedPrice: subtask.fixedPrice ?? "",
    source: subtask.source || ""
  };
};

const editorSubtaskToProject = (subtask) => {
  const payload = {
    title: subtask.name || subtask.title || "",
    description: subtask.description || "",
    weight: subtask.weight || 1,
    estimatedHours: subtask.estimatedHours,
    showDescriptionInTimeReport: subtask.showDescriptionInTimeReport || false,
    requireDescriptionInTimeReport: subtask.requireDescriptionInTimeReport || false,
    registryId: subtask.registryId || subtask.registryRef?.id
  };
  payload.billingModel = subtask.billingModel ? subtask.billingModel : null;
  payload.hourlyRate = subtask.billingModel === "hourly"
    ? (subtask.hourlyRate != null && subtask.hourlyRate !== "" ? parseFloat(subtask.hourlyRate) : null)
    : null;
  payload.fixedPrice = subtask.billingModel === "fixed_price"
    ? (subtask.fixedPrice != null && subtask.fixedPrice !== "" ? parseFloat(subtask.fixedPrice) : null)
    : null;
  if (subtask.source !== undefined && subtask.source !== "") {
    payload.source = subtask.source;
  }
  return payload;
};

/**
 * Modal for viewing and editing task/subtask details
 * - All users can view details and change status
 * - Admins can edit and delete (unless readOnly)
 * 
 * @param {boolean} readOnly - If true, hides edit/delete actions
 */
const TaskSubtaskDetailModal = ({
  isOpen,
  onClose,
  type, // 'task' or 'subtask'
  data,
  bundleId,
  taskIdx,
  subtaskIdx, // only for subtasks
  onUpdate,
  readOnly = false
}) => {
  const { user } = useContext(UserContext);
  const { t } = useT();
  const isAdmin = user?.isAdmin;
  const canEdit = isAdmin && !readOnly;
  const [isEditMode, setIsEditMode] = useState(canEdit);
  const [saving, setSaving] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(data?.status || "todo");

  // Form state for editing
  const [formData, setFormData] = useState({
    title: data?.title || "",
    description: data?.description || "",
    estimatedHours: data?.estimatedHours || "",
    weight: data?.weight || 1,
    billingModel: data?.billingModel || "",
    hourlyRate: data?.hourlyRate ?? "",
    fixedPrice: data?.fixedPrice ?? "",
    source: data?.source || "",
    showDescriptionInTimeReport: data?.showDescriptionInTimeReport || false,
    requireDescriptionInTimeReport: data?.requireDescriptionInTimeReport || false,
    photosNotRequiredInTimeReport: data?.photosNotRequiredInTimeReport || false
  });

  const statusColors = {
    todo: 'bg-gray-100 text-gray-800',
    in_progress: 'bg-blue-100 text-blue-800',
    done: 'bg-green-100 text-green-800',
    blocked: 'bg-red-100 text-red-800'
  };

  const statusLabels = {
    todo: 'To Do',
    in_progress: 'In Progress',
    done: 'Done',
    blocked: 'Blocked'
  };

  // State for subtasks management (only for tasks in edit mode)
  const [subtasks, setSubtasks] = useState([]);
  const [showSubTaskPicker, setShowSubTaskPicker] = useState(false);

  // Initialize subtasks when entering edit mode for a task
  useEffect(() => {
    if (isOpen && type === 'task' && data?.subtasks) {
      if (isEditMode) {
        // Convert project format to editor format
        const editorSubtasks = (data.subtasks || []).map((subtask) =>
          projectSubtaskToEditor(subtask)
        );
        setSubtasks(editorSubtasks);
      } else {
        // Reset when not in edit mode
        setSubtasks([]);
      }
    }
  }, [isOpen, isEditMode, type, data?.subtasks]);

  useEffect(() => {
    if (!isOpen) return;
    setCurrentStatus(data?.status || "todo");
    if (canEdit) {
      setIsEditMode(true);
    } else {
      setIsEditMode(false);
    }
  }, [isOpen, data, canEdit]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen]);

  if (!isOpen || !data) return null;

  // Handler for subtask list changes from SubTaskListEditor
  // This ONLY updates local state - no API calls until Save is clicked
  const handleSubtasksChange = (updatedSubtasks) => {
    // Update local state only - all changes will be saved when user clicks Save Changes
    setSubtasks(updatedSubtasks);
  };

  // Handler for adding subtasks from registry
  // This adds them to local state only - they'll be created when Save is clicked
  const handleAddSubtasksFromRegistry = (selectedSubTasks) => {
    // Convert registry format to editor format and add to local state
    const newSubtasks = selectedSubTasks.map((st, index) => ({
      id: `temp-registry-${Date.now()}-${index}`, // Temp ID for registry items
      name: st.name,
      description: st.description || "",
      weight: st.weight || st.defaultWeight || 1,
      estimatedHours: st.estimatedHours || st.defaultEstimatedHours,
      registryId: st._id,
      showDescriptionInTimeReport: st.showDescriptionInTimeReport || false,
      requireDescriptionInTimeReport: st.requireDescriptionInTimeReport || false,
      billingModel: formData.billingModel || "",
      hourlyRate: "",
      fixedPrice: "",
      isNew: true
    }));

    // Add to local subtasks state
    setSubtasks([...subtasks, ...newSubtasks]);
    setShowSubTaskPicker(false);
  };

  const handleStatusChange = async (newStatus) => {
    if (currentStatus === newStatus) return;
    
    let comment = "";
    if (newStatus === 'blocked') {
      const itemType = type === 'task' ? 'task' : 'subtask';
      comment = prompt(`Please provide a reason for blocking this ${itemType}:`);
      if (!comment) return;
    }

    setUpdating(true);
    try {
      if (type === 'task') {
        await axios.patch(
          `/task-bundles/${bundleId}/tasks/${taskIdx}/status`,
          { toStatus: newStatus, comment }
        );
      } else {
        await axios.patch(
          `/task-bundles/${bundleId}/tasks/${taskIdx}/subtasks/${subtaskIdx}/status`,
          { toStatus: newStatus, comment }
        );
      }
      onUpdate && onUpdate();
      setCurrentStatus(newStatus);
      if (!isEditMode) {
        onClose();
      }
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const handleSave = async () => {
    if (!canEdit) return;

    setSaving(true);
    try {
      // Save task/subtask basic info
      const payload = {
        title: formData.title,
        description: formData.description,
        weight: parseFloat(formData.weight) || 1,
        estimatedHours: formData.estimatedHours ? parseFloat(formData.estimatedHours) : undefined,
        showDescriptionInTimeReport: formData.showDescriptionInTimeReport || false,
        requireDescriptionInTimeReport: formData.requireDescriptionInTimeReport || false
      };

      // Add billing and source fields for tasks and subtasks
      if (type === 'task' || type === 'subtask') {
        payload.billingModel = formData.billingModel || null;
        payload.hourlyRate = formData.billingModel === "hourly"
          ? (formData.hourlyRate !== "" ? parseFloat(formData.hourlyRate) : null)
          : null;
        payload.fixedPrice = formData.billingModel === "fixed_price"
          ? (formData.fixedPrice !== "" ? parseFloat(formData.fixedPrice) : null)
          : null;
        payload.source = formData.source || undefined;
      }

      if (type === 'task') {
        payload.photosNotRequiredInTimeReport = formData.photosNotRequiredInTimeReport || false;
        // Batched save: task + full subtask list in one request
        const cleanedSubtasks = (subtasks || [])
          .filter(st => (st.name || st.title || "").trim())
          .map(st => {
            const base = editorSubtaskToProject(st);
            const idStr = st.id?.toString() || "";
            const isTemp = idStr.startsWith("temp-") || idStr.startsWith("temp-registry-");
            return {
              ...base,
              id: !isTemp ? st.id : undefined,
            };
          });

        await axios.put(`/task-bundles/${bundleId}/tasks/${taskIdx}/with-subtasks`, {
          ...payload,
          subtasks: cleanedSubtasks,
        });
      } else {
        await axios.put(
          `/task-bundles/${bundleId}/tasks/${taskIdx}/subtasks/${subtaskIdx}`,
          payload
        );
      }

      onUpdate && onUpdate();
      setIsEditMode(false);
      onClose();
    } catch (error) {
      console.error("Error saving:", error);
      alert("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canEdit) return;

    const confirmMsg = type === 'task' 
      ? "Delete this task and all its subtasks? This cannot be undone."
      : "Delete this subtask? This cannot be undone.";
    
    if (!window.confirm(confirmMsg)) return;

    try {
      if (type === 'task') {
        await axios.delete(`/task-bundles/${bundleId}/tasks/${taskIdx}`);
      } else {
        await axios.delete(
          `/task-bundles/${bundleId}/tasks/${taskIdx}/subtasks/${subtaskIdx}`
        );
      }

      onUpdate && onUpdate();
      onClose();
    } catch (error) {
      console.error("Error deleting:", error);
      alert("Failed to delete");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-medium text-gray-500 uppercase">
                  {type === 'task' ? 'Task' : 'Subtask'}
                </span>
                {data.status && (
                  <span className={`px-2 py-0.5 text-xs rounded ${statusColors[currentStatus]}`}>
                    {statusLabels[currentStatus]}
                  </span>
                )}
              </div>

              {isEditMode ? (
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full text-xl font-bold px-3 py-2 border rounded"
                  placeholder="Title"
                />
              ) : (
                <h2 className="text-xl font-bold">{data.title}</h2>
              )}
            </div>

            <button
              type="button"
              onClick={onClose}
              className="ml-4 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Description */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            {isEditMode ? (
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border rounded"
                rows={4}
                placeholder="Add a description..."
              />
            ) : (
              <div className="text-gray-800 whitespace-pre-wrap">
                {data.description || <span className="text-gray-400 italic">No description</span>}
              </div>
            )}
          </div>

          {/* Hours: show reported vs estimated, or reported only when no estimate */}
          {!isEditMode && (data.estimatedHours != null || (data.trackedHours != null && data.trackedHours > 0)) && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hours Tracking
              </label>
              <HoursProgressBar 
                estimatedHours={data.estimatedHours} 
                reportedHours={data.trackedHours ?? 0} 
              />
            </div>
          )}

          {/* Metadata */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estimated Hours
              </label>
              {isEditMode ? (
                <input
                  type="number"
                  step="0.5"
                  value={formData.estimatedHours}
                  onChange={(e) => setFormData({ ...formData, estimatedHours: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                />
              ) : (
                <div className="text-gray-800">
                  {data.estimatedHours ? `${data.estimatedHours}h` : '-'}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reported Hours
              </label>
              <div className="text-gray-800">
                {(data.trackedHours != null && data.trackedHours > 0) ? `${data.trackedHours}h` : '-'}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Weight
              </label>
              {isEditMode ? (
                <input
                  type="number"
                  step="0.1"
                  value={formData.weight}
                  onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                />
              ) : (
                <div className="text-gray-800">{data.weight || 1}</div>
              )}
            </div>
          </div>

          {/* Time report description settings - Only in edit mode */}
          {isEditMode && canEdit && (
            <div className="mb-4 border-t pt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Time report description</h3>
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={formData.showDescriptionInTimeReport || false}
                  onChange={(e) => setFormData({ ...formData, showDescriptionInTimeReport: e.target.checked })}
                  className="mt-1 mr-2"
                  disabled={saving}
                />
                <div>
                  <div className="font-medium text-sm">Show description field in time reports</div>
                  <div className="text-xs text-gray-600">
                    When enabled, workers can add a description when logging time
                  </div>
                </div>
              </label>
              {formData.showDescriptionInTimeReport && (
                <label className="flex items-start mt-3 ml-6">
                  <input
                    type="checkbox"
                    checked={formData.requireDescriptionInTimeReport || false}
                    onChange={(e) => setFormData({ ...formData, requireDescriptionInTimeReport: e.target.checked })}
                    className="mt-1 mr-2"
                    disabled={saving}
                  />
                  <div>
                    <div className="font-medium text-sm">Require description in time reports</div>
                    <div className="text-xs text-gray-600">
                      When enabled, workers must enter a description when logging time
                    </div>
                  </div>
                </label>
              )}
            </div>
          )}

          {type === "task" && isEditMode && canEdit && (
            <div className="mb-4 border-t pt-4">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={formData.photosNotRequiredInTimeReport || false}
                  onChange={(e) => setFormData({ ...formData, photosNotRequiredInTimeReport: e.target.checked })}
                  className="mt-1 mr-2"
                  disabled={saving}
                />
                <div>
                  <div className="font-medium text-sm">Photos not required in time reports</div>
                  <div className="text-xs text-gray-600">
                    When enabled, a time report needs no photos if it only contains tasks with this setting. If the report also has tasks that require photos, photos are still required.
                  </div>
                </div>
              </label>
            </div>
          )}

          {/* Billing Configuration Section - show for tasks and subtasks in edit mode */}
          {(type === 'task' || type === 'subtask') && isEditMode && canEdit && (
            <div className="mb-4 border-t pt-4">
              <h3 className="text-lg font-semibold mb-3">Billing Configuration</h3>
              
              {/* Billing Model */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Billing Model
                </label>
                <select
                  value={formData.billingModel}
                  onChange={(e) => setFormData({ ...formData, billingModel: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  disabled={saving}
                >
                  <option value="">Included in parent task</option>
                  <option value="hourly">Hourly/Ongoing</option>
                  <option value="fixed_price">Fixed Price</option>
                </select>
              </div>

              {/* Conditional Hourly Rate */}
              {formData.billingModel === "hourly" && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hourly Rate (kr/hr)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.hourlyRate}
                    onChange={(e) => setFormData({ ...formData, hourlyRate: e.target.value })}
                    placeholder="e.g., 850"
                    className="w-full px-3 py-2 border rounded"
                    disabled={saving}
                  />
                </div>
              )}

              {/* Conditional Fixed Price */}
              {formData.billingModel === "fixed_price" && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fixed Price (kr)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.fixedPrice}
                    onChange={(e) => setFormData({ ...formData, fixedPrice: e.target.value })}
                    placeholder="e.g., 150000"
                    className="w-full px-3 py-2 border rounded"
                    disabled={saving}
                  />
                </div>
              )}

              {/* Source */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("taskBuilder.source")}
                </label>
                <select
                  value={formData.source}
                  onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  disabled={saving}
                >
                  <option value="">{t("taskBuilder.sourceNotSet")}</option>
                  <option value="original_offer">{t("taskBuilder.sourceOriginalOffer")}</option>
                  <option value="variations_ata">{t("taskBuilder.sourceVariationsAta")}</option>
                </select>
              </div>
            </div>
          )}

          {/* Subtasks Section - Only show for tasks in edit mode */}
          {type === 'task' && isEditMode && canEdit && (
            <div className="mb-4 border-t pt-4">
              <SubTaskListEditor
                subTasks={subtasks}
                onChange={handleSubtasksChange}
                mode="project"
                showSaveToRegistry={false}
                onAddFromRegistry={() => setShowSubTaskPicker(true)}
                disabled={saving}
                defaultBillingModel={formData.billingModel || ""}
              />
            </div>
          )}

          {/* Status Change (all users) */}
          <div className={`mb-4 p-4 rounded ${isEditMode ? "border border-gray-200 bg-white" : "bg-gray-50"}`}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleStatusChange('todo')}
                disabled={updating}
                className={`flex-1 px-3 py-2 text-sm rounded transition-colors ${
                  currentStatus === 'todo'
                    ? 'bg-gray-200 text-gray-900 font-medium'
                    : 'bg-white border border-gray-300 hover:bg-gray-50'
                }`}
              >
                To Do
              </button>
              <button
                type="button"
                onClick={() => handleStatusChange('in_progress')}
                disabled={updating}
                className={`flex-1 px-3 py-2 text-sm rounded transition-colors ${
                  currentStatus === 'in_progress'
                    ? 'bg-blue-200 text-blue-900 font-medium'
                    : 'bg-white border border-gray-300 hover:bg-gray-50'
                }`}
              >
                In Progress
              </button>
              <button
                type="button"
                onClick={() => handleStatusChange('done')}
                disabled={updating}
                className={`flex-1 px-3 py-2 text-sm rounded transition-colors ${
                  currentStatus === 'done'
                    ? 'bg-green-200 text-green-900 font-medium'
                    : 'bg-white border border-gray-300 hover:bg-gray-50'
                }`}
              >
                Done
              </button>
              <button
                type="button"
                onClick={() => handleStatusChange('blocked')}
                disabled={updating}
                className={`flex-1 px-3 py-2 text-sm rounded transition-colors ${
                  currentStatus === 'blocked'
                    ? 'bg-red-200 text-red-900 font-medium'
                    : 'bg-white border border-gray-300 hover:bg-gray-50'
                }`}
              >
                Blocked
              </button>
            </div>
          </div>

          {/* Notes (subtasks only) */}
          {type === 'subtask' && data.notes && !isEditMode && (
            <div className="mb-4 p-3 bg-yellow-50 rounded">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <div className="text-sm text-gray-800 whitespace-pre-wrap">
                {data.notes}
              </div>
            </div>
          )}

          {/* Photos */}
          {data.workPhotos && data.workPhotos.length > 0 && !isEditMode && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Work Photos ({data.workPhotos.length})
              </label>
              <div className="grid grid-cols-4 gap-2">
                {data.workPhotos.map((photoUrl, idx) => (
                  <img
                    key={idx}
                    src={toCloudinaryDeliveryUrl(photoUrl, "c_fill,g_auto,w_240,h_240,q_auto,f_auto/")}
                    alt={`Work photo ${idx + 1}`}
                    className="w-full h-20 object-cover rounded"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Event History */}
          {data.events && data.events.length > 0 && !isEditMode && (
            <div className="mb-4">
              <details className="text-sm">
                <summary className="cursor-pointer font-medium text-gray-700 hover:text-gray-900">
                  Event History ({data.events.length})
                </summary>
                <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                  {data.events.slice().reverse().map((event, idx) => (
                    <div key={idx} className="text-xs p-2 bg-gray-50 rounded">
                      <div className="font-medium text-gray-900">
                        {new Date(event.timestamp).toLocaleString()}
                        {event.userId && (
                          <span className="ml-2 text-gray-600 font-normal">
                            - {event.userId.firstname} {event.userId.lastname}
                          </span>
                        )}
                      </div>
                      <div className="text-gray-600">
                        {event.fromStatus && `${event.fromStatus} → `}
                        {event.toStatus}
                      </div>
                      {event.comment && (
                        <div className="text-gray-700 mt-1">{event.comment}</div>
                      )}
                    </div>
                  ))}
                </div>
              </details>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between items-center pt-4 border-t">
            {canEdit && !isEditMode && (
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="danger"
                  size="md"
                  onClick={handleDelete}
                >
                  Delete
                </Button>
              </div>
            )}

            {isEditMode && (
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="solid"
                  size="md"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="md"
                  onClick={onClose}
                  disabled={saving}
                >
                  Cancel
                </Button>
              </div>
            )}

            {!canEdit && !isEditMode && (
              <div className="flex-1" />
            )}

            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </div>

      {/* SubTask Picker Modal for adding from registry */}
      {type === 'task' && (
        <SubTaskPickerModal
          isOpen={showSubTaskPicker}
          onClose={() => setShowSubTaskPicker(false)}
          onSelect={handleAddSubtasksFromRegistry}
          existingIds={(data.subtasks || [])
            .map(st => st.registryRef?.id)
            .filter(Boolean)}
        />
      )}
    </div>
  );
};

export default TaskSubtaskDetailModal;
